<template>
  <div class="app-shell" :class="{ 'dark-theme': isDark }">
    <nav class="navbar">
      <div class="nav-container">
        <router-link to="/" class="logo">
          <span class="logo-icon">
            <svg viewBox="0 0 40 40" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
              <!-- 月亮 -->
              <defs>
                <linearGradient id="moonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style="stop-color:#fbbf24;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#f59e0b;stop-opacity:1" />
                </linearGradient>
                <linearGradient id="glowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:0.3" />
                  <stop offset="100%" style="stop-color:#8b5cf6;stop-opacity:0.1" />
                </linearGradient>
              </defs>
              <!-- 光晕背景 -->
              <circle cx="20" cy="20" r="18" fill="url(#glowGradient)" />
              <!-- 月亮主体 -->
              <path d="M20 4 C12 4 6 12 6 20 C6 28 12 36 20 36 C14 32 14 8 20 4" fill="url(#moonGradient)" />
              <!-- 上升箭头/光芒 -->
              <path d="M28 18 L32 12 L36 18" stroke="#3b82f6" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M32 14 L32 26" stroke="#3b82f6" stroke-width="2.5" fill="none" stroke-linecap="round" />
              <!-- 小星星点缀 -->
              <circle cx="26" cy="8" r="1.5" fill="#fbbf24" />
              <circle cx="34" cy="28" r="1" fill="#3b82f6" />
            </svg>
          </span>
          <span class="logo-text">{{ $t('site.name') }}</span>
        </router-link>
        <div class="nav-right">
          <div class="nav-links">
            <router-link to="/">{{ $t('nav.home') }}</router-link>
            <router-link to="/json">{{ $t('nav.json') }}</router-link>
            <router-link to="/timestamp">{{ $t('nav.timestamp') }}</router-link>
            <router-link to="/wordcount">{{ $t('nav.wordcount') }}</router-link>
            <router-link to="/blog">实操指南</router-link>
          </div>

          <button class="theme-toggle" @click="toggleTheme" :title="isDark ? $t('theme.light') : $t('theme.dark')">
            <span v-if="isDark">☀️</span>
            <span v-else>🌙</span>
          </button>
        </div>
      </div>
    </nav>
    <main class="main-content">
      <router-view :key="$route.path" />
      <ToolGuide />
    </main>
    <footer class="footer">

      <div class="footer-links">
        <router-link to="/blog">{{ $t('nav.blog') }}</router-link>
        <span class="divider">|</span>
        <router-link to="/about">{{ $t('nav.about') }}</router-link>
        <span class="divider">|</span>
        <router-link to="/privacy">{{ $t('nav.privacy') }}</router-link>
      </div>
      <div class="footer-links"><router-link to="/contact">联系与反馈</router-link><span class="divider">|</span><router-link to="/terms">使用条款</router-link></div>
      <p>© {{ new Date().getFullYear() }} 月升工具网 · 工具与结果都值得核对</p>
    </footer>
  </div>
</template>

<script>
import ToolGuide from './components/ToolGuide.vue'

export default {
  name: 'App',
  components: {
    ToolGuide
  },
  data() {
    return {
      isDark: false
    }
  },
  mounted() {
    // 读取本地存储的主题偏好
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      this.isDark = savedTheme === 'dark'
    } else {
      // 检测系统主题偏好
      this.isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    }
  },
  methods: {
    toggleTheme() {
      this.isDark = !this.isDark
      localStorage.setItem('theme', this.isDark ? 'dark' : 'light')
    }
  }
}
</script>

<style>
:root {
  /* 明亮主题变量 */
  --bg-primary: #f8fafc;
  --bg-secondary: #ffffff;
  --bg-tertiary: #f1f5f9;
  --bg-card: #ffffff;
  --bg-input: #f8fafc;
  --bg-hover: #e2e8f0;

  --text-primary: #1e293b;
  --text-secondary: #64748b;
  --text-muted: #94a3b8;

  --border-color: #e2e8f0;
  --border-light: #f1f5f9;

  --accent-color: #3b82f6;
  --accent-hover: #2563eb;
  --accent-light: rgba(59, 130, 246, 0.1);

  --success-color: #22c55e;
  --error-color: #ef4444;
  --warning-color: #f59e0b;

  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.07);
  --shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.1);

  --gradient-bg: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.dark-theme {
  --bg-primary: #0f172a;
  --bg-secondary: #1e293b;
  --bg-tertiary: #334155;
  --bg-card: rgba(15, 23, 42, 0.8);
  --bg-input: #0c1220;
  --bg-hover: #334155;

  --text-primary: #f8fafc;
  --text-secondary: #cbd5e1;
  --text-muted: #94a3b8;

  --border-color: rgba(255, 255, 255, 0.1);
  --border-light: rgba(255, 255, 255, 0.05);

  --accent-color: #3b82f6;
  --accent-hover: #60a5fa;
  --accent-light: rgba(59, 130, 246, 0.15);

  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.3);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.4);
  --shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.5);

  --gradient-bg: linear-gradient(135deg, #1a1c2e 0%, #2d3561 100%);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  min-height: 100vh;
}

.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--gradient-bg);
  color: var(--text-primary);
  transition: background 0.3s, color 0.3s;
}

.navbar {
  background: var(--bg-secondary);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: var(--shadow-sm);
  transition: background 0.3s, border-color 0.3s;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;
  transition: color 0.3s;
}

.logo-icon {
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-icon svg {
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
  transition: transform 0.3s;
}

.logo:hover .logo-icon svg {
  transform: scale(1.1);
}

.nav-links {
  display: flex;
  gap: 8px;
}

.nav-links a {
  color: var(--text-secondary);
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 14px;
}

.nav-links a:hover {
  color: var(--text-primary);
  background: var(--bg-hover);
}

.nav-links a.router-link-exact-active {
  color: var(--accent-color);
  background: var(--accent-light);
}

.theme-toggle {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s;
}

.theme-toggle:hover {
  background: var(--bg-hover);
  transform: scale(1.05);
}

.main-content {
  flex: 1;
  padding: 40px 20px;
}

.footer {
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  padding: 20px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  transition: background 0.3s, border-color 0.3s;
}

.donate-section {
  margin-bottom: 16px;
}

.footer-links {
  margin-bottom: 12px;
}

.footer-links a {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-links a:hover {
  color: var(--accent-color);
}

.footer-links .divider {
  margin: 0 12px;
  color: var(--text-muted);
}

@media (max-width: 768px) {
  .nav-container {
    flex-direction: column;
    height: auto;
    padding: 12px 20px;
    gap: 12px;
  }

  .nav-right {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }

  .nav-links {
    flex-wrap: wrap;
    justify-content: center;
  }

  .nav-links a {
    padding: 6px 12px;
    font-size: 13px;
  }

  .theme-toggle {
    position: absolute;
    top: 12px;
    right: 20px;
  }
}
</style>
